/**
 * All widget CSS lives in this single string, applied inside the shadow root.
 * `:host { all: initial }` blocks inherited properties from the host page;
 * the shadow boundary blocks page selectors. Everything the widget needs is
 * declared explicitly below.
 */
export const WIDGET_CSS = `
:host {
  all: initial;
}

*,
*::before,
*::after {
  box-sizing: border-box;
}

.bf-launcher {
  position: fixed;
  right: 20px;
  bottom: 20px;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 9999px;
  background: #1e293b;
  color: #ffffff;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.25);
  transition: transform 0.15s ease, background 0.15s ease;
  font-family: system-ui, -apple-system, 'Segoe UI', sans-serif;
}

.bf-launcher:hover {
  background: #0f172a;
  transform: scale(1.06);
}

.bf-launcher:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 2px;
}

.bf-launcher svg {
  width: 22px;
  height: 22px;
  display: block;
}

.bf-launcher .bf-icon-close {
  display: none;
}

.bf-launcher.bf-active {
  background: #b91c1c;
}

.bf-launcher:disabled,
.bf-launcher.bf-disabled {
  opacity: 0.6;
  cursor: default;
  pointer-events: none;
}

.bf-launcher.bf-active .bf-icon-bubble {
  display: none;
}

.bf-launcher.bf-active .bf-icon-close {
  display: block;
}

.bf-highlight {
  position: fixed;
  pointer-events: none;
  border: 2px solid #2563eb;
  background: rgba(37, 99, 235, 0.12);
  border-radius: 2px;
  z-index: 2;
  display: none;
}

.bf-highlight-label {
  position: fixed;
  pointer-events: none;
  background: #1e293b;
  color: #ffffff;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
  line-height: 1;
  padding: 4px 6px;
  border-radius: 4px;
  z-index: 3;
  display: none;
  max-width: 60vw;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bf-hint {
  position: fixed;
  left: 50%;
  bottom: 20px;
  transform: translateX(-50%);
  background: #1e293b;
  color: #ffffff;
  font-family: system-ui, -apple-system, 'Segoe UI', sans-serif;
  font-size: 13px;
  line-height: 1.4;
  padding: 8px 14px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.25);
  display: none;
}

.bf-dialog {
  position: fixed;
  display: none;
  /* <dialog> UA defaults fight an anchored box: margin:auto re-centres it and
     dialog:modal pins inset-block, which would stretch it to the full height. */
  margin: 0;
  right: auto;
  bottom: auto;
  width: 480px;
  max-width: calc(100vw - 32px);
  background: #ffffff;
  color: #0f172a;
  font-family: system-ui, -apple-system, 'Segoe UI', sans-serif;
  font-size: 14px;
  line-height: 1.5;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.22);
  padding: 14px;
  min-width: min(320px, calc(100vw - 32px));
  min-height: min(240px, calc(100vh - 32px));
  max-height: calc(100vh - 32px);
  resize: both;
  overflow: auto;
  flex-direction: column;
}

.bf-dialog[open] {
  display: flex;
}

/* The dialog is modal to keep the host page's focus traps off it, not to dim
   the page — the element the feedback is about has to stay fully visible. */
.bf-dialog::backdrop {
  background: transparent;
}

.bf-dialog .bf-selector {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
  color: #64748b;
  background: #f1f5f9;
  border-radius: 6px;
  padding: 4px 8px;
  margin-bottom: 10px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bf-dialog label {
  display: block;
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 4px;
}

.bf-toolbar {
  display: flex;
  gap: 2px;
  margin-bottom: 4px;
}

.bf-toolbar button {
  min-width: 26px;
  height: 24px;
  padding: 0 6px;
  border: 1px solid transparent;
  border-radius: 5px;
  background: transparent;
  color: #64748b;
  font-family: inherit;
  font-size: 12px;
  line-height: 1;
  cursor: pointer;
}

.bf-toolbar button:hover {
  background: #f1f5f9;
  color: #0f172a;
}

.bf-toolbar button:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 1px;
}

.bf-toolbar .bf-fmt-bold {
  font-weight: 800;
}

.bf-toolbar .bf-fmt-italic {
  font-style: italic;
  font-family: Georgia, serif;
}

.bf-toolbar .bf-fmt-code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
}

.bf-toolbar-spacer {
  flex: 1;
}

.bf-toolbar button[aria-pressed='true'] {
  background: #e2e8f0;
  color: #0f172a;
}

.bf-dialog-full {
  left: 16px !important;
  top: 16px !important;
  right: 16px;
  bottom: 16px;
  width: auto !important;
  height: auto !important;
  max-width: none;
  max-height: none;
  resize: none;
}

.bf-preview {
  flex: 1;
  min-height: 140px;
  overflow: auto;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 8px 10px;
}

.bf-preview h1 { font-size: 1.25em; margin: 0.4em 0 0.2em; }
.bf-preview h2 { font-size: 1.15em; margin: 0.4em 0 0.2em; }
.bf-preview h3 { font-size: 1.05em; margin: 0.4em 0 0.2em; }
.bf-preview p { margin: 0.3em 0; }
.bf-preview ul { margin: 0.3em 0; padding-left: 1.2em; }
.bf-preview code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  background: #f1f5f9;
  border-radius: 4px;
  padding: 0 4px;
}
.bf-preview a { color: #2563eb; }

.bf-dialog textarea {
  width: 100%;
  min-height: 140px;
  resize: none;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 8px 10px;
  font: inherit;
  color: inherit;
  background: #ffffff;
  outline: none;
  flex: 1;
}

.bf-dialog textarea:focus-visible {
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
}

.bf-dialog .bf-email {
  width: 100%;
  margin-top: 6px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 6px 10px;
  font: inherit;
  font-size: 13px;
  color: inherit;
  background: #ffffff;
  outline: none;
}

.bf-dialog .bf-email:focus-visible {
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
}

.bf-dialog .bf-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 12px;
}

.bf-btn {
  border: none;
  border-radius: 8px;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  padding: 7px 14px;
  cursor: pointer;
}

.bf-btn-primary {
  background: #2563eb;
  color: #ffffff;
}

.bf-btn-primary:hover {
  background: #1d4ed8;
}

.bf-btn-primary:disabled {
  opacity: 0.6;
  cursor: default;
}

.bf-btn-secondary {
  background: #f1f5f9;
  color: #0f172a;
}

.bf-btn-secondary:hover {
  background: #e2e8f0;
}

.bf-btn:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 2px;
}

.bf-status {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  margin-top: 10px;
}

.bf-status.bf-status-success {
  color: #15803d;
}

.bf-status.bf-status-error {
  color: #b91c1c;
}

.bf-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid #cbd5e1;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: bf-spin 0.7s linear infinite;
}

@keyframes bf-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .bf-launcher {
    transition: none;
  }
  .bf-spinner {
    animation-duration: 1.5s;
  }
}

@media (prefers-color-scheme: dark) {
  .bf-dialog {
    background: #1e293b;
    color: #f1f5f9;
    border-color: #334155;
  }
  .bf-dialog .bf-selector {
    background: #0f172a;
    color: #94a3b8;
  }
  .bf-dialog textarea {
    background: #0f172a;
    color: #f1f5f9;
    border-color: #475569;
  }
  .bf-dialog .bf-email {
    background: #0f172a;
    color: #f1f5f9;
    border-color: #475569;
  }
  .bf-btn-secondary {
    background: #334155;
    color: #f1f5f9;
  }
  .bf-btn-secondary:hover {
    background: #475569;
  }
  .bf-toolbar button {
    color: #94a3b8;
  }
  .bf-toolbar button:hover {
    background: #334155;
    color: #f1f5f9;
  }
  .bf-toolbar button[aria-pressed='true'] {
    background: #475569;
    color: #f1f5f9;
  }
  .bf-preview {
    background: #0f172a;
    border-color: #475569;
  }
}
`;
